import React, { FC } from 'react';
import { OpenCampaignCardAspectRatio } from '@browsing/campaign';
import { Campaign } from '@shared/types/mylounge';
import { useGridLayoutContext } from '../context/GridLayoutContext';
import { StyledOpenCampaignCard, StyledPlusExclusiveCampaign } from '../styles';

type SlotType =
    | 'SQUARE'
    | 'LARGE_SQUARE'
    | 'HORIZONTAL_RECTANGLE'
    | 'VERTICAL_RECTANGLE'
    | 'MOBILE';

export interface SlotSize {
    aspect: OpenCampaignCardAspectRatio;
    colSpan: number;
    rowSpan: number;
}

export const Slot: Record<SlotType, SlotSize> = {
    SQUARE: { aspect: OpenCampaignCardAspectRatio.Square, colSpan: 1, rowSpan: 1 },
    LARGE_SQUARE: { aspect: OpenCampaignCardAspectRatio.LargeSquare, colSpan: 2, rowSpan: 2 },
    HORIZONTAL_RECTANGLE: {
        aspect: OpenCampaignCardAspectRatio.HorizontalRect,
        colSpan: 2,
        rowSpan: 1,
    },
    VERTICAL_RECTANGLE: {
        aspect: OpenCampaignCardAspectRatio.VerticalRect,
        colSpan: 1,
        rowSpan: 2,
    },
    MOBILE: { aspect: OpenCampaignCardAspectRatio.Mobile, colSpan: 2, rowSpan: 1 },
};

export interface CampaignBlockProps {
    slots?: SlotSize[];
    campaignsInternal?: Campaign[];
    /**
     * Starting index for campaigns in this block.
     * Explicitly passed to avoid mutable state in context.
     * Default: 0 (will be set by DeclarativeGrid during traversal)
     */
    startIndex?: number;
}

/**
 * Renders a block of campaign cards within the grid layout.
 *
 * This component consumes campaigns from the `GridLayoutContext` and renders them
 * according to the specified slot configuration. Each slot defines the aspect ratio
 * and grid span (columns and rows) for the campaign card.
 *
 * Special handling is applied for Plus exclusive campaigns, which are rendered
 * using a dedicated `StyledPlusExclusiveCampaign` component at a calculated position.
 *
 * @param props - The component props
 * @param props.slots - Array of slot configurations defining the layout for each campaign card.
 *                      Each slot specifies the aspect ratio, column span, and row span.
 *                      Defaults to a single `HORIZONTAL_RECTANGLE` slot.
 *
 * @example
 * // Render a single horizontal rectangle campaign
 * <CampaignBlock />
 *
 * @example
 * // Render multiple campaigns with different layouts
 * <CampaignBlock slots={[Slot.LARGE_SQUARE, Slot.VERTICAL_RECTANGLE, Slot.SQUARE]} />
 */
export const CampaignBlock: FC<CampaignBlockProps> = ({
    slots = [Slot.HORIZONTAL_RECTANGLE],
    /**
     * Starting index for campaigns in this block.
     * Injected by DeclarativeGrid during traversal.
     * Default: 0 (when used outside DeclarativeGrid)
     */
    startIndex = 0,
}) => {
    const { campaigns, columnCount, isPlusEnabled } = useGridLayoutContext();

    return (
        <>
            {slots.map((slot, slotIndex) => {
                const currentIndex = startIndex + slotIndex;
                const campaign =
                    currentIndex < campaigns.length ? campaigns[currentIndex] : undefined;
                if (!campaign) return null;

                const { aspect, colSpan, rowSpan } = slot;

                if (campaign.plusBenefit?.type === 'EXCLUSIVE') {
                    return (
                        <StyledPlusExclusiveCampaign
                            key={campaign.campaignId}
                            $colSpan={colSpan}
                            $rowSpan={rowSpan}
                            aspectRatio={aspect}
                            position={`${currentIndex + 1}|${columnCount}`}
                        />
                    );
                }

                return (
                    <StyledOpenCampaignCard
                        key={campaign.campaignId}
                        $colSpan={colSpan}
                        $rowSpan={rowSpan}
                        aspectRatio={aspect}
                        campaign={campaign}
                        isPlusEnabled={isPlusEnabled}
                        position={`${currentIndex + 1}|${columnCount}`}
                    />
                );
            })}
        </>
    );
};
